import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Package,
  FileText,
  Image,
  Edit3,
  Trash2,
  Save,
  X,
  MapPin,
  Receipt,
} from "lucide-react";
import axios from "axios";
import "../styles/SaleDetails.css";
import Modal from "../components/Admin/Modal/Modal";
import { useNotification } from "../hooks/useNotification";
import { getColourPresentation } from "../utils/colourPresentation";

function SaleDetails() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { saleData, previousView } = location.state || {};
  const { showSuccess, showError } = useNotification();

  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    notes: "",
    receiptNumber: "",
    items: [],
  });
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const fetchSaleDetails = useCallback(async (saleId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`/api/sales/${saleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        const saleData = response.data.sale;

        // Add originalQuantity to items for validation
        const saleWithOriginalQuantities = {
          ...saleData,
          items: saleData.items.map((item) => ({
            ...item,
            originalQuantity: item.quantity,
          })),
        };
        setSale(saleWithOriginalQuantities);
        setEditForm({
          notes: saleData.notes || "",
          receiptNumber: saleData.receipt_number || "",
          items: saleData.items.map((item) => ({
            ...item,
            originalQuantity: item.quantity,
          })),
        });
      } else {
        setError(t("se.saleDetails.errorFetch"));
      }
    } catch (err) {
      console.error("Fetch sale details error:", err);
      setError(err.response?.data?.error || t("se.saleDetails.errorFetch"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (saleData) {
      fetchSaleDetails(saleData.sale_id);
    } else {
      setError(t("se.saleDetails.noSaleData"));
      setLoading(false);
    }
  }, [fetchSaleDetails, saleData, t]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset form to original values
    setEditForm({
      notes: sale.notes || "",
      receiptNumber: sale.receipt_number || "",
      items: sale.items.map((item) => ({
        ...item,
        quantity: item.originalQuantity || item.quantity,
      })),
    });
  };

  const handleUpdate = async () => {
    try {
      setUpdating(true);
      const token = localStorage.getItem("token");

      const updateData = {
        items: editForm.items.map((item) => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: parseInt(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          serial_number: item.serial_number,
        })),
        notes: editForm.notes,
        receipt_number: editForm.receiptNumber,
      };

      const response = await axios.put(
        `/api/sales/${sale.sale_id}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        // Add originalQuantity to items for future edits
        const updatedSale = {
          ...response.data.data,
          items: response.data.data.items.map((item) => ({
            ...item,
            originalQuantity: item.quantity,
          })),
        };
        setSale(updatedSale);
        setIsEditing(false);
        // Update editForm with new data
        setEditForm({
          notes: response.data.data.notes || "",
          receiptNumber: response.data.data.receipt_number || "",
          items: response.data.data.items.map((item) => ({
            ...item,
            originalQuantity: item.quantity,
          })),
        });
        showSuccess(t("se.saleDetails.successUpdated", { defaultValue: "Sale updated successfully!" }));
      } else {
        setError(t("se.saleDetails.errorUpdate"));
      }
    } catch (err) {
      console.error("Update sale error:", err);
      const message = err.response?.data?.error || t("se.saleDetails.errorUpdate");
      setError(message);
      showError(message);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const token = localStorage.getItem("token");
      await axios.delete(`/api/sales/${sale.sale_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showSuccess(t("se.sale.successDeleted"));
      navigate("/promoter", { state: { view: "SALES" } });
    } catch (err) {
      console.error("Delete sale error:", err);
      const message = err.response?.data?.error || t("se.saleDetails.errorDelete");
      setError(message);
      showError(message);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-MY", {
      style: "currency",
      currency: "MYR",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-MY", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="sale-details-container">
        <div className="sale-details-skeleton-card" aria-hidden="true">
          <div className="sale-details-skeleton-header"></div>
          <div className="sale-details-skeleton-line sale-details-skeleton-line-lg"></div>
          <div className="sale-details-skeleton-grid">
            <div className="sale-details-skeleton-panel"></div>
            <div className="sale-details-skeleton-panel"></div>
            <div className="sale-details-skeleton-panel"></div>
          </div>
          <div className="sale-details-skeleton-title"></div>
          <div className="sale-details-skeleton-item"></div>
          <div className="sale-details-skeleton-item"></div>
        </div>
      </div>
    );
  }

  if (error || !sale) {
    return (
      <div className="sale-details-container">
        <div className="error-state">
          <p>
            {error || t("se.saleDetails.noSaleData")}.{" "}
            {t("se.saleDetails.tryAgain")}
          </p>
          <button
            onClick={() =>
              navigate("/promoter", {
                state: { view: previousView || "DASHBOARD" },
              })
            }
            className="back-button"
          >
            <ArrowLeft size={16} />
            {t("se.saleDetails.goBack")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sale-details-container">
      <div className="se-header">
        <div className="se-header-title">
          <button
            onClick={() =>
              navigate("/promoter", {
                state: { view: previousView || "DASHBOARD" },
              })
            }
            className="se-back-btn"
          >
            <ArrowLeft size={24} />
          </button>
          <span className="sale-title">
            {t("se.saleDetails.title", { id: sale.sale_id })}
          </span>
        </div>
        <div className="header-actions">
          {!isEditing ? (
            <>
              <button
                onClick={handleEdit}
                className="edit-button"
                aria-label="Edit sale"
              >
                <Edit3 size={20} />
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="delete-button"
                disabled={deleting}
                aria-label="Delete sale"
              >
                <Trash2 size={20} />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleCancelEdit}
                className="cancel-button"
                aria-label="Cancel edit"
              >
                <X size={20} />
              </button>
              <button
                onClick={handleUpdate}
                className="save-button"
                disabled={updating}
                aria-label="Save changes"
              >
                <Save size={20} />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="sale-details-content">
        <div className="sale-info-card">
          <div className="sale-info-header">
            <div className="sale-date">
              <Calendar size={16} />
              <span>{formatDate(sale.sale_date)}</span>
            </div>
            <div
              className={`sale-status-badge ${
                editForm.items?.some((item) => Number(item.quantity) < 0) ||
                Number(sale.total_amount) < 0
                  ? "return"
                  : "sale"
              }`}
            >
              {editForm.items?.some((item) => Number(item.quantity) < 0) ||
              Number(sale.total_amount) < 0
                ? t("se.history.returnLabel")
                : t("se.history.soldLabel")}
            </div>
          </div>

          <div className="sale-info-body">
            <div className="sale-info-summary-grid">
              <div className="sale-info-panel sale-info-panel-highlight">
                <div className="sale-info-panel-icon">
                  <MapPin size={18} />
                </div>
                <div className="sale-info-panel-content">
                  <div className="sale-info-panel-label">
                    {t("se.saleDetails.outlet")}
                  </div>
                  <div className="sale-info-panel-value">
                    {sale.outlet?.bp_outlet_name ||
                      sale.checkIn?.outlet_name ||
                      t("se.saleDetails.unknownOutlet")}
                  </div>
                </div>
              </div>

              <div
                className={`sale-info-panel sale-info-panel-total ${
                  editForm.items?.some((item) => Number(item.quantity) < 0) ||
                  Number(sale.total_amount) < 0
                    ? "return"
                    : "sale"
                }`}
              >
                <div className="sale-info-panel-icon">
                  <Receipt size={18} />
                </div>
                <div className="sale-info-panel-content">
                  <div className="sale-info-panel-label">
                    {t("se.saleDetails.totalAmount")}
                  </div>
                  <div className="sale-info-panel-value sale-info-panel-value-amount">
                    {isEditing
                      ? formatCurrency(
                          editForm.items.reduce(
                            (sum, item) =>
                              sum +
                              (parseInt(item.quantity) || 0) *
                                (parseFloat(item.unit_price) || 0),
                            0,
                          ),
                        )
                      : formatCurrency(sale.total_amount)}
                  </div>
                </div>
              </div>
            </div>

            <div className="sale-info-panel">
              <div className="sale-info-panel-icon">
                <FileText size={18} />
              </div>
              <div className="sale-info-panel-content">
                <div className="sale-info-panel-label">
                  {t("se.saleDetails.notes")}
                </div>
                {isEditing ? (
                  <textarea
                    value={editForm.notes}
                    onChange={(e) =>
                      setEditForm({ ...editForm, notes: e.target.value })
                    }
                    placeholder={t("se.saleDetails.notesPlaceholder")}
                    rows="3"
                    className="edit-textarea"
                  />
                ) : (
                  <div className="sale-info-panel-value sale-info-panel-value-muted">
                    {sale.notes || t("se.saleDetails.noNotes")}
                  </div>
                )}
              </div>
            </div>

            <div className="sale-info-panel">
              <div className="sale-info-panel-icon">
                <Receipt size={18} />
              </div>
              <div className="sale-info-panel-content">
                <div className="sale-info-panel-label">
                  {t("se.saleDetails.receiptNumber")}
                </div>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.receiptNumber}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        receiptNumber: e.target.value,
                      })
                    }
                    placeholder={t("se.saleDetails.receiptNumberPlaceholder")}
                    className="edit-input"
                  />
                ) : (
                  <div className="sale-info-panel-value sale-info-panel-value-muted">
                    {sale.receipt_number || t("se.saleDetails.notProvided")}
                  </div>
                )}
              </div>
            </div>

            {sale.receipt_image_url && (
              <div className="sale-info-panel">
                <div className="sale-info-panel-icon">
                  <Image size={18} />
                </div>
                <div className="sale-info-panel-content">
                  <div className="sale-info-panel-label">
                    {t("se.saleDetails.receipt")}
                  </div>
                  <a
                    href={sale.receipt_signed_url || sale.receipt_image_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="receipt-link"
                  >
                    {t("se.saleDetails.viewReceipt")}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Items Section */}
        <div className="sale-items-card">
          <div className="items-header">
            <Package size={16} />
            <h3>
              {editForm.items?.some((item) => Number(item.quantity) < 0) ||
              Number(sale.total_amount) < 0
                ? t("se.saleDetails.itemsReturned")
                : t("se.saleDetails.itemsSold")}
            </h3>
          </div>

          <div className="items-list">
            {editForm.items && editForm.items.length > 0 ? (
              editForm.items.map((item, index) => (
                <div key={index} className="item-row">
                  <div className="item-info">
                    <div className="item-row-header">
                      <div className="item-heading-block">
                        <div className="item-name">
                          {item.product_model_code || item.product_name}
                        </div>
                        {(item.product_model_description || item.product_name) && (
                          <div className="item-subtitle">
                            {item.product_model_description || item.product_name}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="item-details">
                      {item.colour && (
                        <div className="detail-row">
                          <span className="item-label">
                            {t("se.saleForm.colourHelperLabel", { defaultValue: "Colour:" })}
                          </span>
                          <span
                            className="item-detail-colour"
                            style={getColourPresentation(item.colour)?.badgeStyle}
                          >
                            {getColourPresentation(item.colour)?.swatchStyle ? (
                              <span
                                className="item-detail-colour-swatch"
                                style={getColourPresentation(item.colour).swatchStyle}
                                aria-hidden="true"
                              />
                            ) : null}
                            <span>{item.colour}</span>
                          </span>
                        </div>
                      )}
                      <div className="detail-row">
                        <span className="item-label">
                          {t("se.saleDetails.quantity")}:
                        </span>
                        {isEditing ? (
                          <div className="quantity-input-container">
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => {
                                const newItems = [...editForm.items];
                                const newValue = e.target.value;

                                // Allow empty value temporarily, but validate on blur
                                if (newValue === "") {
                                  newItems[index].quantity = "";
                                } else {
                                  const numValue = parseInt(newValue) || 0;
                                  // Ensure quantity is between 1 and original quantity
                                  if (
                                    numValue >= 1 &&
                                    numValue <= item.originalQuantity
                                  ) {
                                    newItems[index].quantity = numValue;
                                  }
                                  // If invalid, keep the current value
                                }

                                setEditForm({ ...editForm, items: newItems });
                              }}
                              onBlur={(e) => {
                                const newItems = [...editForm.items];
                                const numValue = parseInt(e.target.value) || 0;

                                // On blur, ensure minimum of 1
                                if (numValue < 1) {
                                  newItems[index].quantity = 1;
                                } else if (numValue > item.originalQuantity) {
                                  newItems[index].quantity =
                                    item.originalQuantity;
                                }

                                setEditForm({ ...editForm, items: newItems });
                              }}
                              className="quantity-input"
                              placeholder="1"
                            />
                            <small className="quantity-hint">
                              {t("se.saleDetails.quantityHint", {
                                max: item.originalQuantity,
                              })}
                            </small>
                          </div>
                        ) : (
                          <span className="item-quantity item-detail-emphasis">{item.quantity}</span>
                        )}
                      </div>
                      <div className="detail-row">
                        <span className="item-label">
                          {t("se.saleDetails.unitPrice")}:
                        </span>
                        {isEditing ? (
                          <div
                            className="price-input-container"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <span
                              style={{ color: "#6b7280", fontSize: "14px" }}
                            >
                              RM
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.unit_price}
                              onChange={(e) => {
                                const newItems = [...editForm.items];
                                newItems[index].unit_price = e.target.value;
                                setEditForm({ ...editForm, items: newItems });
                              }}
                              className="quantity-input"
                              style={{ width: "100px" }}
                              placeholder="0.00"
                            />
                          </div>
                        ) : (
                          <span className="item-price">
                            {formatCurrency(item.unit_price)}
                          </span>
                        )}
                      </div>
                      <div className="detail-row">
                        <span className="item-label">
                          {t("se.saleDetails.total")}:
                        </span>
                        <span className="item-total">
                          {formatCurrency(
                            (item.quantity || 0) * (item.unit_price || 0),
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="item-serial-section">
                      <span className="item-label">
                        {t("se.saleDetails.serialNumber")}:
                      </span>
                      {isEditing ? (
                        <input
                          type="text"
                          value={item.serial_number || ""}
                          onChange={(e) => {
                            const newItems = [...editForm.items];
                            newItems[index].serial_number = e.target.value;
                            setEditForm({ ...editForm, items: newItems });
                          }}
                          placeholder={t("se.saleDetails.serialPlaceholder")}
                          className="serial-input"
                        />
                      ) : (
                        <span className="item-serial">
                          {item.serial_number ||
                            t("se.saleDetails.notProvided")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-items">{t("se.saleDetails.noItems")}</div>
            )}
          </div>
        </div>
      </div>
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => !deleting && setShowDeleteConfirm(false)}
        size="small"
      >
        <div className="sale-delete-confirm">
          <h3 className="sale-delete-confirm-title">
            {t("se.saleDetails.deleteConfirmTitle", {
              defaultValue: "Delete this sale?",
            })}
          </h3>
          <p className="sale-delete-confirm-text">
            {t("se.saleDetails.deleteConfirm")}
          </p>
          <div className="sale-delete-confirm-actions">
            <button
              type="button"
              className="sale-delete-cancel"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleting}
            >
              {t("se.dialog.cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="button"
              className="sale-delete-submit"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting
                ? t("se.saleDetails.deleting", { defaultValue: "Deleting..." })
                : t("se.saleDetails.deleteAction", { defaultValue: "Delete Sale" })}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default SaleDetails;
